import React, { useMemo } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import PrimeCheckbox from 'components/form/PrimeCheckbox';
import { AdminSearchForm } from 'components/form/admin-search-form/AdminSearchForm';
import {
  EPD_FORMAT,
  epdFormatLookup,
  EPDStatusDisplayNameDataSource,
  registrationDateSortOptions,
} from 'components/v2/compiler/constants';
import InputFormField from 'components/v2/form/InputFormField';
import SelectDataField from 'components/v2/form/select-data-field/SelectDataField';
import AuthService from 'services/AuthService';
import { FeatureFlagKeyDynamicFormsPortal } from 'services/api/featureFlagsApi';
import { useFeatureFlags } from 'services/api/queries';

interface AdminSearchFormPanelProps {
  onSubmit: (values: any) => void;
  isLoading: boolean;
  companiesLookup: any[];
  licenseesLookup: any[];
  mergedAgentsLookup: any[];
  isCompaniesLookupLoading: boolean;
  isLicenseesLookupLoading: boolean;
  isMergedAgentsLookupLoading: boolean;
  isSearchComplete: boolean;
}

const AdminSearchFormPanel: React.FC<AdminSearchFormPanelProps> = ({
  onSubmit,
  isLoading,
  companiesLookup,
  licenseesLookup,
  mergedAgentsLookup,
  isCompaniesLookupLoading,
  isLicenseesLookupLoading,
  isMergedAgentsLookupLoading,
  isSearchComplete,
}) => {
  const initialValues = {
    registrationNumber: '',
    title: '',
    companyId: null,
    licenseeId: null,
    agentId: null,
    epdFormat: null,
    status: null,
    excludeCopyGroupEpds: false,
    registrationDateSortOrder: null,
    excludeEPDsWithoutRegistrationDate: false,
  };

  const validationSchema = Yup.object({
    registrationNumber: Yup.number().nullable().typeError('Only numbers are allowed'),
    title: Yup.string().nullable(),
    companyId: Yup.string().nullable(),
    licenseeId: Yup.object().nullable(),
    agentId: Yup.object().nullable(),
    status: Yup.object().nullable(),
    registrationDateSortOrder: Yup.object().nullable(),
  });

  const filterDisplayMap = {
    epdFormat: (val: string) => epdFormatLookup.find((o) => o.value === val)?.label ?? val,
    companyId: (val: string) => companiesLookup.labelByValue(val) ?? val,
    licenseeId: (val: string) => licenseesLookup.find((c) => c.value === val)?.label ?? val,
    agentId: (val: string) => mergedAgentsLookup.find((c) => c.value === val)?.label ?? val,
    status: (val: string) => EPDStatusDisplayNameDataSource.find((o) => o.value === val)?.label ?? val,
    excludeCopyGroupEpds: (val?: boolean) => (val ? 'Exclude Copy group EPDs' : ''),
    registrationDateSortOrder: (val: string) => registrationDateSortOptions.find((o) => o.value === val)?.label ?? val,
    excludeEPDsWithoutRegistrationDate: (val?: boolean) => (val ? 'Exclude EPDs without a registration date' : ''),
  };

  const isAdmin = AuthService.isAdmin();

  const { data: featureFlags } = useFeatureFlags();
  const isDynamicFormsEnabled = featureFlags?.[FeatureFlagKeyDynamicFormsPortal] === true;

  const epdFormatOptions = useMemo(
    () => (isDynamicFormsEnabled ? epdFormatLookup : epdFormatLookup.filter((o) => o.value !== EPD_FORMAT.DYNAMIC_FORMS)),
    [isDynamicFormsEnabled]
  );

  const handleSubmit = (values: any) => {
    const transformedValues = { ...values };

    if (values.registrationDateSortOrder) {
      transformedValues.sortBy = 'RegistrationDate';
      transformedValues.sortDirection = values.registrationDateSortOrder;
    } else {
      transformedValues.sortBy = null;
      transformedValues.sortDirection = null;
    }

    delete transformedValues.registrationDateSortOrder;

    onSubmit(transformedValues);
  };

  return (
    <AdminSearchForm
      title="Filters"
      displayMap={filterDisplayMap}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isSearchComplete={isSearchComplete}
    >
      <StyledSearchPanel>
        <InputFormField label="Registration number" placeholder="Type here" name="registrationNumber" maxLength={90} />
        <InputFormField label="EPD name" placeholder="Type here" name="title" maxLength={90} />
        <SelectDataField
          isClearable
          label="Organisation"
          name="companyId"
          isVirtualized
          options={companiesLookup}
          isLoading={isCompaniesLookupLoading}
          placeholder="Select"
        />
        {isAdmin && (
          <>
            <SelectDataField
              isClearable
              label="Licensee"
              name="licenseeId"
              options={licenseesLookup}
              isLoading={isLicenseesLookupLoading}
              placeholder="Select"
            />
            <SelectDataField
              isClearable
              label="Agent"
              name="agentId"
              options={mergedAgentsLookup}
              isLoading={isMergedAgentsLookupLoading}
              placeholder="Select"
            />
          </>
        )}
        <SelectDataField isClearable label="EPD format" name="epdFormat" options={epdFormatOptions} placeholder="Select" />
        <div>
          <SelectDataField
            isClearable
            label="status"
            name="status"
            options={EPDStatusDisplayNameDataSource}
            placeholder="Select"
          />
          <CheckBoxGroup>
            <PrimeCheckbox name="excludeCopyGroupEpds" label="Exclude Copy group EPDs" />
          </CheckBoxGroup>
        </div>
        <div>
          <SelectDataField
            isClearable
            tooltip="The date when the EPD Owner submitted this EPD version for publication by clicking “Send to publish”."
            label="sort by registration date"
            name="registrationDateSortOrder"
            options={registrationDateSortOptions}
            placeholder="Select"
          />
          <CheckBoxGroup>
            <PrimeCheckbox name="excludeEPDsWithoutRegistrationDate" label="Exclude EPDs without a registration date" />
          </CheckBoxGroup>
        </div>
      </StyledSearchPanel>
    </AdminSearchForm>
  );
};

const StyledSearchPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckBoxGroup = styled.div`
  margin-top: 4px;
  & label {
    font-size: 14px;
  }
  max-height: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  .p-checkbox,
  .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
  }
  .p-checkbox .p-checkbox-box .p-checkbox-icon {
    font-size: 10px;
    font-weight: 700;
  }
  .p-checkbox-box.p-highlight.p-disabled {
    background-color: #757575;
    border-color: #757575;
  }
`;

export default AdminSearchFormPanel;
